<template>
  <div class="weather-map-container">
    <!-- Mobile-friendly hamburger menu -->
    <div class="map-controls" :class="{ 'controls-open': controlsOpen }">

      <button class="controls-toggle" @click="controlsOpen = !controlsOpen">
        <svg v-if="controlsOpen" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
        </svg>
      </button>
      
      <div class="controls-panel">

       



        <!-- Playback - more compact -->
        <div class="control-group compact">
          <div class="d-flex align-items-center gap-2">
            <label class="mb-0 flex-shrink-0">Playback</label>
            <div class="btn-group btn-group-sm flex-grow-1" role="group">
              <button @click="decrementTime" class="btn btn-outline-secondary btn-sm" title="Previous">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M.5 3.5A.5.5 0 0 1 1 4v3.248l6.267-3.636c.52-.302 1.233.043 1.233.696v2.94l6.267-3.636c.52-.302 1.233.043 1.233.696v7.384c0 .653-.713.998-1.233.696L8.5 8.752v2.94c0 .653-.713.998-1.233.696L1 8.752V12a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5z"/>
                </svg>
              </button>
              <button @click="toggleMoviePlay" class="btn btn-outline-secondary btn-sm">
                <svg v-if="isPlaying" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M5.5 3.5A1.5 1.5 0 0 1 7 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5zm5 0A1.5 1.5 0 0 1 12 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5z"/>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                  <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
                </svg>
              </button>
              <button @click="incrementTime" class="btn btn-outline-secondary btn-sm">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M15.5 3.5a.5.5 0 0 1 .5.5v8a.5.5 0 0 1-1 0V8.752l-6.267 3.636c-.52.302-1.233-.043-1.233-.696v-2.94l-6.267 3.636C.713 12.69 0 12.345 0 11.692V4.308c0-.653.713-.998 1.233-.696L7.5 7.248v-2.94c0-.653.713-.998 1.233-.696L15 7.248V4a.5.5 0 0 1 .5-.5z"/>
                </svg>
              </button>
              <button @click="resetToInitialTime" class="btn btn-outline-secondary btn-sm">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                  <path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                  <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Speed + Time Step inline -->
        <div class="control-row">
          <div class="control-group compact">
            <label>Speed: {{ playbackSpeed }}x</label>
            <input type="range" v-model="playbackSpeed" min="0.5" max="3" step="0.5" 
                   @input="adjustPlaybackSpeed" class="form-range">
          </div>
          <div class="control-group compact">
            <div class="d-flex justify-content-between align-items-center">
              <label class="mb-0">Step: {{ currentTimeSkip }}m</label>
              <div class="form-check form-switch mb-0 ms-1">
                <input type="checkbox" class="form-check-input" id="auto-skip-toggle" v-model="autoTimeSkip">
              </div>
            </div>
            <div class="d-flex gap-1 mt-1" v-if="!autoTimeSkip">
              <div class="small fs-sm text-muted align-self-center">Manual: </div>
              <button @click="decreaseTimeSkip" class="btn btn-sm btn-outline-primary py-0 px-1">−</button>
              <button @click="increaseTimeSkip" class="btn btn-sm btn-outline-primary py-0 px-1">+</button>
            </div>
            <div v-else class="small fs-sm text-muted mt-1">Auto time skip</div>

          </div>
        </div>

        <!-- Weather Layers -->
        <div class="control-group">
          <div class="d-flex justify-content-between align-items-center">
            <label class="fw-bold mb-0">Layers</label>
            <button @click="showAddLayerModal = true" class="btn btn-sm btn-outline-primary py-0 px-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
              </svg>
            </button>
          </div>
          <div class="layer-list">
            <div v-for="(layer, index) in activeLayers" :key="layer.id" class="layer-item-compact">
              <div class="d-flex align-items-center gap-1">
                <input type="checkbox" class="form-check-input m-0" v-model="layer.visible" @change="toggleLayerVisibility(layer)">
                <span class="layer-label flex-grow-1">{{ getElementDisplayName(layer.element) }}</span>
                <input v-if="layer.visible" type="range" v-model.number="layer.opacity" min="0" max="1" step="0.1"
                       @input="updateLayerOpacity(layer)" class="form-range form-range-sm opacity-slider">
                <button @click="removeLayer(index)" class="btn btn-link text-danger p-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Overlays -->
        <div class="control-group compact">
          <label class="fw-bold">Overlays</label>
          <div class="d-flex gap-3 align-items-center">
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" id="cities-toggle" v-model="showGeoJson" @change="toggleGeoJsonLayer">
              <label class="form-check-label small" for="cities-toggle">Wx</label>
            </div>
            <select v-if="showGeoJson" v-model="geoJsonElement" @change="refreshGeoJsonTiles" class="form-control form-control-sm" style="width: auto; min-width: 80px;">
              <option value="temp">Temp</option>
              <option value="precip">Precip</option>
              <option value="wind">Wind</option>
            </select>
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" id="labels-toggle" v-model="showLabels" @change="toggleLabels">
              <label class="form-check-label small" for="labels-toggle">Labels</label>
            </div>
          </div>
        </div>
        
        <!-- Markers section -->
        <div class="control-group compact">
          <div class="d-flex justify-content-between align-items-center">
            <label class="fw-bold mb-0">Markers</label>
            <button @click="goToUserLocation" class="btn btn-sm btn-outline-secondary py-0 px-1" title="Go to my location">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
              </svg>
            </button>
          </div>
          <div class="marker-input-row">
            <input type="text" v-model="newMarkerLatLng" @keyup.enter="addMarkerFromInput" 
                   class="form-control form-control-sm" placeholder="lat, lng">
            <button @click="addMarkerFromInput" class="btn btn-sm btn-outline-primary" title="Add marker">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
              </svg>
            </button>
            <button @click="addMarkerFromGPS" class="btn btn-sm btn-outline-secondary" title="Add from GPS">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 0a.5.5 0 0 1 .5.5v.518A7.001 7.001 0 0 1 14.982 7.5h.518a.5.5 0 0 1 0 1h-.518A7.001 7.001 0 0 1 8.5 14.982v.518a.5.5 0 0 1-1 0v-.518A7.001 7.001 0 0 1 1.018 8.5H.5a.5.5 0 0 1 0-1h.518A7.001 7.001 0 0 1 7.5 1.018V.5A.5.5 0 0 1 8 0zM8 3a5 5 0 1 0 0 10A5 5 0 0 0 8 3z"/>
              </svg>
            </button>
          </div>
          <div v-if="userMarkers.length" class="marker-list">
            <div v-for="(m, i) in userMarkers" :key="m.id" class="marker-item">
              <span class="marker-dot" :style="{ background: m.color }"></span>
              <span class="marker-coords">{{ m.lat.toFixed(3) }}, {{ m.lng.toFixed(3) }}</span>
              <button @click="removeMarker(i)" class="btn btn-link text-danger p-0">×</button>
            </div>
          </div>
        </div>

        <!-- Advanced section - collapsed by default -->
        <div class="control-group compact">
          <div class="advanced-header" @click="showAdvanced = !showAdvanced">
            <label class="fw-bold mb-0" style="cursor: pointer;">Advanced</label>
            <svg v-if="showAdvanced" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </div>
          <div v-if="showAdvanced" class="advanced-content">
            <div class="control-group compact">
              <label>Units</label>
              <select v-model="unitGroup" class="form-control form-control-sm">
                <option value="us">US</option>
                <option value="metric">Metric</option>
              </select>
            </div>
            <div class="mb-2">
              <label >Server</label>
              <select v-model="baseApiUrl" class="form-control form-control-sm">
                <option value="https://weather5.visualcrossing.com">Weather5</option>
                <option value="http://localhost:8080">Localhost</option>
              </select>
            </div>
            <!-- Base map -->
            <div class="control-group compact">
              <label>Base Map</label>
              <select v-model="selectedBaseMap" @change="changeBaseMap" class="form-control form-control-sm">
                <option value="carto-light">Light</option>
                <option value="carto-dark">Dark</option>
                <option value="osm">OpenStreetMap</option>
                <option value="vcosm1">VC OSM 1</option>
                <option value="vcosm2">VC OSM 2</option>
              </select>
            </div>
            <div class="d-flex align-items-center gap-2">
              <small class="text-muted">{{ viewInfo.centerPair }}</small>
              <small class="text-muted">z{{ viewInfo.zoom }}</small>
              <button @click="copyCenterToClipboard" class="btn btn-sm btn-link p-0" title="Copy coordinates">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                  <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Layer Modal -->
    <div v-if="showAddLayerModal" class="add-layer-modal" @click.self="showAddLayerModal = false">
      <div class="modal-content-custom">
        <h6>Add Weather Layer</h6>
        
        <div class="mb-3">
          <label class="form-label small">Model / Source</label>
          <select v-model="newLayerModel" @change="onModelChange" class="form-control form-control-sm">
            <option v-for="(config, modelId) in modelOptions" :key="modelId" :value="modelId">
              {{ getModelDisplayName(modelId) }}
            </option>
          </select>
        </div>
        
        <div class="mb-3">
          <label class="form-label small">Element</label>
          <select v-model="newLayerElement" class="form-control form-control-sm">
            <option v-for="element in availableElements" :key="element" :value="element">
              {{ getElementDisplayName(element) }}
            </option>
          </select>
        </div>
        
        <div class="d-flex gap-2">
          <button @click="showAddLayerModal = false" class="btn btn-secondary btn-sm flex-fill">Cancel</button>
          <button @click="addLayer" class="btn btn-primary btn-sm flex-fill" :disabled="!newLayerElement">Add</button>
        </div>
      </div>
    </div>

    <!-- Map container -->
    <div id="map" class="map-view">
      <!-- Time display -->
      <div class="time-display" @click="showDateTimePicker = true">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
          <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
        </svg>
        {{ formattedTime }}
      </div>

      <!-- DateTime picker overlay -->
      <div v-if="showDateTimePicker" class="datetime-picker-modal">
        <div class="modal-content-custom">
          <h6>Select Date & Time (UTC)</h6>
          <input type="datetime-local" :value="dateTimeLocalValue" 
                 @change="onDateTimeInput" class="form-control">
          <div class="d-flex gap-2 mt-2">
            <button @click="showDateTimePicker = false" class="btn btn-secondary btn-sm flex-fill">Cancel</button>
            <button @click="applyDateTime" class="btn btn-primary btn-sm flex-fill">Apply</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Legend (positioned on map) - now shows all visible layers -->
    <div v-if="visibleLayerLegends.length > 0" class="legend-container" :class="{ 'legend-collapsed': legendCollapsed }">
      <div class="legend-header" @click="legendCollapsed = !legendCollapsed">
        <strong>Legend</strong>
        <svg v-if="legendCollapsed" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
        </svg>
      </div>
      <div v-if="!legendCollapsed" class="legend-content">
        <div v-for="legend in visibleLayerLegends" :key="legend.layerId" class="legend-item">
          <div class="legend-item-header" @click="toggleLegendExpand(legend.layerId)">
            <span class="legend-item-title">{{ legend.title }}</span>
            <svg v-if="expandedLegends[legend.layerId]" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </div>
          <div v-if="expandedLegends[legend.layerId]" class="legend-item-content" v-html="legend.html"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  name: "WeatherMapAi",
  data() {
    return {
      map: null,
      mapReady: false,
      controlsOpen: false,
      legendCollapsed: false,
      showAdvanced: false,
      expandedLegends: {}, // Track which legend items are expanded
      
      // Model definitions with elements and time settings
      modelOptions: {
        default: { 
          elements: ["precipcomposite", "preciprate", "preciptype", "precip", "hailsize", "temp", "cloudcover", "sealevelpressure", "windspeed", "winddir", "wind", "alerts", "cloudtopheight", "cloudtoptemp", "cloudopticaldepth", "cloudphase", "solarradiation", "abi_rad_c02", "abi_rad_c07"], 
          timeSkip: 10,
          timeSkipOverride: false,
          loopWindow: { startOffset: -120, endOffset: 120 }
        },
        goes_10min_location_2: { 
          elements: ["cloudtopheight", "cloudtoptemp", "cloudopticaldepth", "cloudphase", "solarradiation", "abi_rad_c02", "abi_rad_c07", "preciprate", "cloudmask", "total_precipitable_water", "cloudcover", "lowcloudcover", "medcloudcover", "hicloudcover", "precip_index"], 
          timeSkip: 10,
          timeSkipOverride: false,
          loopWindow: { startOffset: -120, endOffset: 0 }
        },
        mrms_2min_location_2: { 
          elements: ["precipcomposite", "preciprate", "preciptype", "hailsize", "reflectivity"], 
          timeSkip: 2,
          timeSkipOverride: false,
          loopWindow: { startOffset: -120, endOffset: 0 }
        },
        ukmet_15min_location_2: { 
          elements: ["preciprate"], 
          timeSkip: 15,
          timeSkipOverride: false,
          loopWindow: { startOffset: -120, endOffset: 0 }
        },
        dwd_5min_location_2: { 
          elements: ["precipcomposite", "preciprate", "preciptype", "reflectivity"], 
          timeSkip: 2,
          timeSkipOverride: false,
          loopWindow: { startOffset: -120, endOffset: 0 }
        },
        hrrr_location_2: { 
          elements: ["precipcomposite", "preciprate", "preciptype", "precip", "temp", "feelslike", "dew", "humidity", "windspeed", "winddir", "wind"], 
          timeSkip: 60,
          loopWindow: { startOffset: 0, endOffset: 7200 }
        },
        iconeu_location_2: { 
          elements: ["precipcomposite", "preciprate", "preciptype", "precip", "temp", "feelslike", "dew", "humidity", "windspeed", "winddir", "wind"], 
          timeSkip: 60,
          loopWindow: { startOffset: 0, endOffset: 7200 }
        },
        gfs_location_2: { 
          elements: ["precipcomposite", "preciprate", "preciptype", "precip", "temp", "feelslike", "dew", "humidity", "cloudcover", "windspeed", "winddir", "wind"], 
          timeSkip: 60,
          loopWindow: { startOffset: 0, endOffset: 7200 }
        },
        ecmwf_location_2: { 
          elements: ["temp", "feelslike", "dew", "humidity", "preciprate", "precip", "cloudcover", "windspeed", "winddir", "wind"], 
          timeSkip: 60,
          loopWindow: { startOffset: 0, endOffset: 7200 }
        },
        ecmwfaifs_location_2: { 
          elements: ["temp", "feelslike", "dew", "humidity", "preciprate", "cloudcover", "windspeed", "winddir", "wind"], 
          timeSkip: 60,
          loopWindow: { startOffset: 0, endOffset: 7200 }
        },
        weatherRadarSubhourly_location: { 
          elements: ["reflectivity"], 
          timeSkip: 10,
          loopWindow: { startOffset: -120, endOffset: 120 }
        }
      },
      
      // Element display names
      elementNames: {
        precipcomposite: "Precipitation Composite",
        preciprate: "Precipitation Rate",
        preciptype: "Precipitation Type",
        precip: "Precipitation",
        hailsize: "Hail Size",
        temp: "Temperature",
        feelslike: "Feels Like",
        dew: "Dew Point",
        humidity: "Humidity",
        cloudcover: "Cloud Cover",
        sealevelpressure: "Sea Level Pressure",
        windspeed: "Wind Speed",
        winddir: "Wind Direction",
        wind: "Wind",
        alerts: "Weather Alerts",
        cloudtopheight: "Cloud Top Height",
        cloudtoptemp: "Cloud Top Temperature",
        cloudopticaldepth: "Cloud Optical Depth",
        cloudphase: "Cloud Phase",
        solarradiation: "Solar Radiation",
        abi_rad_c02: "ABI Radiance C02",
        abi_rad_c07: "ABI Radiance C07",
        reflectivity: "Radar Reflectivity"
      },
      
      // Model display names
      modelNames: {
        default: "Default",
        goes_10min_location_2: "GOES Satellite",
        mrms_2min_location_2: "MRMS Radar",
        ukmet_15min_location_2: "UK Met Office",
        dwd_5min_location_2: "DWD Radar",
        hrrr_location_2: "HRRR",
        iconeu_location_2: "ICON-EU",
        gfs_location_2: "GFS",
        ecmwf_location_2: "ECMWF",
        ecmwfaifs_location_2: "ECMWF AIFS",
        weatherRadarSubhourly_location: "Weather Radar"
      },
      
      // Active layers on the map
      activeLayers: [],
      nextLayerId: 1,
      
      // Add layer modal
      showAddLayerModal: false,
      newLayerModel: 'mrms_2min_location_2',
      newLayerElement: 'precipcomposite',
      
      // Base maps
      selectedBaseMap: 'carto-light',
      baseMaps: {
        'carto-light': {
          url: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png',
          attribution: '&copy; <a href="https://carto.com">CARTO</a>'
        },
        'carto-dark': {
          url: 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',
          attribution: '&copy; <a href="https://carto.com">CARTO</a>'
        },
        'osm': {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        },
        'satellite': {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: 'Tiles &copy; Esri'
        },
        'vcosm1': {
          url: 'https://osm1.visualcrossing.com/osm/{z}/{x}/{y}.png',
          attribution: 'Tiles &copy; VC'
        },
        'vcosm2': {
          url: 'https://osm2.visualcrossing.com/osm/{z}/{x}/{y}.png',
          attribution: 'Tiles &copy; VC'
        }
      },
      baseLayer: null,
      labelLayer: null,
      showLabels: false,
      
      // GeoJSON cities layer
      showGeoJson: false,
      geoJsonElement: 'temp',
      geoJsonLayer: null,
      geoJsonTiles: {},
      geoJsonAbortControllers: new Map(),
      
      // Time management
      currentTime: null,
      isPlaying: false,
      playbackSpeed: 1.0,
      movieInterval: null,
      showDateTimePicker: false,
      tempDateTimeValue: '',
      autoTimeSkip: true,
      manualTimeSkip: 10,
      timeSkipPresets: [1, 2, 5, 10, 15, 30, 60],
      // Settings
      unitGroup: 'metric',
      baseApiUrl: 'https://weather5.visualcrossing.com',
      weatherApiKey: '4UXQMWSDV5KEMM6JR2SC2FC8P',
      
      // UI state
      viewInfo: {
        lat: null,
        lng: null,
        zoom: null,
        centerPair: ''
      },
      
      // Legend - now stores legends for all visible layers
      visibleLayerLegends: [],
      elementBinCache: {},

      // User markers
      userMarkers: [],
      nextMarkerId: 1,
      markerColors: ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'],
      newMarkerLatLng: '',
      userLocationMarker: null,
      userLocation: null
    };
  },
  
  computed: {
    formattedTime() {
      return this.currentTime ? this.formatFullDateTime(this.currentTime) : '';
    },
    dateTimeLocalValue() {
      if (!this.currentTime) return '';
      const pad = n => String(n).padStart(2, '0');
      const y = this.currentTime.getUTCFullYear();
      const m = pad(this.currentTime.getUTCMonth() + 1);
      const d = pad(this.currentTime.getUTCDate());
      const h = pad(this.currentTime.getUTCHours());
      const min = pad(this.currentTime.getUTCMinutes());
      return `${y}-${m}-${d}T${h}:${min}`;
    },
    availableElements() {
      const model = this.modelOptions[this.newLayerModel];
      return model ? model.elements : [];
    },
    // Get the smallest timeSkip from visible layers for playback
    activeTimeSkip() {
      const visibleLayers = this.activeLayers.filter(l => l.visible);
      if (visibleLayers.length === 0) return 10;
      return Math.min(...visibleLayers.map(l => this.modelOptions[l.model]?.timeSkip || 10));
    },
    currentTimeSkip() {
      return this.autoTimeSkip ? this.activeTimeSkip : this.manualTimeSkip;
    }
  },
  
  watch: {
    unitGroup() {
      if (!this.mapReady) return;
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
      this.saveSettings();
    },
    baseApiUrl() {
      if (!this.mapReady) return;
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
      this.saveSettings();
    },
    selectedBaseMap() {
      this.saveSettings();
    },
    showLabels() {
      this.saveSettings();
    },
    showGeoJson() {
      this.saveSettings();
    },
    geoJsonElement() {
      this.saveSettings();
    },
    playbackSpeed() {
      this.saveSettings();
    },
    autoTimeSkip() {
      this.saveSettings();
    },
    manualTimeSkip() {
      this.saveSettings();
    },
    activeLayers: {
      deep: true,
      handler() {
        if (this.mapReady) {
          this.saveSettings();
        }
      }
    }
  },
  
  mounted() {
    this.loadSettings(); // Load saved settings first
    this.initializeMap();
    this.currentTime = this.getLastInterval();
    this.mapReady = true;
    
    // Add a default layer only if none were loaded from storage
    if (this.activeLayers.length === 0) {
      this.addDefaultLayers();
    } else {
      // Create Leaflet layers for loaded layers
      this.activeLayers.forEach(layer => {
        if (layer.visible) {
          this.createLeafletLayer(layer);
          this.expandedLegends[layer.id] = true;
        }
      });
      this.updateAllLegends();
    }
    
    // Apply saved overlay settings
    if (this.showLabels) {
      this.addLabelsLayer();
    }
    if (this.showGeoJson) {
      this.addGeoJsonTileLayer();
    }
    
    this.$nextTick(() => {
      this.updateViewInfo();
    });
  },
  
  beforeUnmount() {
    this.stopMovie();
    // Clean up user markers and their tooltips
    this.userMarkers.forEach(marker => {
      if (marker.leafletMarker) {
        marker.leafletMarker.unbindTooltip();
        this.map.removeLayer(marker.leafletMarker);
      }
    });
    
    // Clean up GeoJSON tiles
    this.geoJsonAbortControllers.forEach(ctrl => ctrl.abort());
    this.geoJsonAbortControllers.clear();
    Object.keys(this.geoJsonTiles).forEach(key => {
      if (this.geoJsonTiles[key] && this.map) {
        this.map.removeLayer(this.geoJsonTiles[key]);
      }
    });
    
    if (this.map) {
      this.map.remove();
    }
  },
  
  methods: {
     initializeMap() {
      this.map = L.map("map", {
        zoomControl: false
      }).setView([0, 0], 7);
      
      L.control.zoom({ position: 'topleft' }).addTo(this.map);

      // Add custom layers toggle control
      const self = this;
      const LayersToggle = L.Control.extend({
        options: {
          position: 'topleft'
        },
        onAdd: function() {
          const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
          const button = L.DomUtil.create('a', 'layers-control-btn', container);
          button.href = '#';
          button.title = 'Toggle Layers';
          button.setAttribute('role', 'button');
          button.setAttribute('aria-label', 'Toggle Layers');
          button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.235 1.559a.5.5 0 0 0-.47 0l-7.5 4a.5.5 0 0 0 0 .882L3.188 8 .264 9.559a.5.5 0 0 0 0 .882l7.5 4a.5.5 0 0 0 .47 0l7.5-4a.5.5 0 0 0 0-.882L12.813 8l2.922-1.559a.5.5 0 0 0 0-.882l-7.5-4zM8 9.433 1.562 6 8 2.567 14.438 6 8 9.433z"/>
          </svg>`;
          
          L.DomEvent.on(button, 'click', function(e) {
            L.DomEvent.preventDefault(e);
            L.DomEvent.stopPropagation(e);
            self.controlsOpen = !self.controlsOpen;
          });
          
          return container;
        }
      });
      
      new LayersToggle().addTo(this.map);
      // Create panes FIRST
      this.map.createPane('weatherPane');
      this.map.getPane('weatherPane').style.zIndex = 400;
      
      this.map.createPane('labelsPane');
      this.map.getPane('labelsPane').style.zIndex = 500;
      
      this.map.createPane('citiesPane');
      this.map.getPane('citiesPane').style.zIndex = 600;
      
      this.map.createPane('markersPane');
      this.map.getPane('markersPane').style.zIndex = 650;

      // Then initialize base map
      this.changeBaseMap();
      
      this.map.on('moveend', this.updateViewInfo);
      this.map.on('zoomend', this.updateViewInfo);
      
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            this.userLocation = { lat: latitude, lng: longitude };
            this.map.setView([latitude, longitude], 10);
             this.addCustomMarker(latitude, longitude, "#4285f4");
          },
          (error) => console.error("Geolocation error:", error)
        );
      }
    },
    
    
    addDefaultLayers() {
      // Add MRMS Radar as default
      this.activeLayers.push({
        id: this.nextLayerId++,
        model: 'default',
        element: 'precipcomposite',
        visible: true,
        opacity: 0.7,
        leafletLayer: null
      });
      
      this.activeLayers.forEach(layer => {
        if (layer.visible) {
          this.createLeafletLayer(layer);
          // Expand first layer's legend by default
          this.expandedLegends[layer.id] = true;
        }
      });
      
      this.updateAllLegends();
    },
    /*
     addUserLocationMarker(lat, lng) {
      if (this.userLocationMarker) {
        this.map.removeLayer(this.userLocationMarker);
      }
      
      const icon = L.divIcon({
        className: 'user-location-marker',
        html: `<div style="width:16px;height:16px;background:#4285f4;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      
      this.userLocationMarker = L.marker([lat, lng], { 
        icon, 
        pane: 'markersPane',
        zIndexOffset: 1000 
      }).addTo(this.map);
      
      this.userLocationMarker.bindTooltip('Your Location', { direction: 'top', offset: [0, -10] });
    },
    */
    addMarkerFromInput() {
      const input = this.newMarkerLatLng.trim();
      if (!input) return;
      
      // Parse "lat,lng" or "lat lng"
      const parts = input.split(/[,\s]+/).map(s => parseFloat(s.trim()));
      if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        this.addCustomMarker(parts[0], parts[1]);
        this.newMarkerLatLng = '';
      }
    },
    
    addMarkerFromGPS() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            this.addCustomMarker(latitude, longitude);
          },
          (error) => console.error("Geolocation error:", error)
        );
      }
    },
    
    addCustomMarker(lat, lng, color) {
      color = color || this.markerColors[this.userMarkers.length % this.markerColors.length];
      const id = this.nextMarkerId++;
      
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:14px;height:14px;background:${color};border:2px solid white;border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7]
      });
      
      const marker = L.marker([lat, lng], { 
        icon, 
        pane: 'markersPane',
        draggable: true 
      }).addTo(this.map);
      
      marker.bindTooltip(`${lat.toFixed(4)}, ${lng.toFixed(4)}`, { direction: 'top', offset: [0, -8] });
      
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        const m = this.userMarkers.find(m => m.id === id);
        if (m) {
          m.lat = pos.lat;
          m.lng = pos.lng;
          marker.setTooltipContent(`${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)}`);
        }
      });
      
      this.userMarkers.push({ id, lat, lng, color, leafletMarker: marker });
    },
    
    removeMarker(index) {
      const marker = this.userMarkers[index];
      if (marker.leafletMarker) {
        // Unbind tooltip before removing to prevent animation errors
        marker.leafletMarker.unbindTooltip();
        this.map.removeLayer(marker.leafletMarker);
      }
      this.userMarkers.splice(index, 1);
    },
    
    goToUserLocation() {
      if (this.userLocation) {
        this.map.setView([this.userLocation.lat, this.userLocation.lng], 10);
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            this.userLocation = { lat: latitude, lng: longitude };
            this.map.setView([latitude, longitude], 10);
            this.addUserLocationMarker(latitude, longitude);
          }
        );
      }
    },
    getModelDisplayName(modelId) {
      return this.modelNames[modelId] || modelId;
    },
    
    getElementDisplayName(element) {
      return this.elementNames[element] || element;
    },
    
    onModelChange() {
      // Reset element to first available when model changes
      const elements = this.availableElements;
      this.newLayerElement = elements.length > 0 ? elements[0] : '';
    },
    
    addLayer() {
      if (!this.newLayerElement) return;
      
      const layer = {
        id: this.nextLayerId++,
        model: this.newLayerModel,
        element: this.newLayerElement,
        visible: true,
        opacity: 0.7,
        leafletLayer: null
      };
      
      this.activeLayers.push(layer);
      this.createLeafletLayer(layer);
      // Auto-expand new layer's legend
      this.expandedLegends[layer.id] = true;
      this.updateAllLegends();
      this.showAddLayerModal = false;
    },
    
    removeLayer(index) {
      const layer = this.activeLayers[index];
      if (layer.leafletLayer) {
        this.map.removeLayer(layer.leafletLayer);
      }
      delete this.expandedLegends[layer.id];
      this.activeLayers.splice(index, 1);
      this.updateAllLegends();
    },
    
    toggleLayerVisibility(layer) {
      if (layer.visible) {
        if (!layer.leafletLayer) {
          this.createLeafletLayer(layer);
        } else {
          layer.leafletLayer.addTo(this.map);
        }
        // Auto-expand when made visible
        this.expandedLegends[layer.id] = true;
      } else {
        if (layer.leafletLayer) {
          this.map.removeLayer(layer.leafletLayer);
        }
      }
      this.updateAllLegends();
    },
    
    toggleLegendExpand(layerId) {
      this.expandedLegends[layerId] = !this.expandedLegends[layerId];
    },
    
    createLeafletLayer(layer) {
      const tileUrl = this.buildTileUrl(layer.element, layer.model, this.currentTime);
      
      layer.leafletLayer = L.tileLayer(tileUrl, {
        opacity: layer.opacity,
        pane: 'weatherPane',
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.visualcrossing.com">Visual Crossing</a>'
      }).addTo(this.map);
    },
    
    updateLayerOpacity(layer) {
      if (layer.leafletLayer) {
        layer.leafletLayer.setOpacity(layer.opacity);
      }
    },
    
    updateLayerTiles(layer, time) {
      if (!layer.leafletLayer) return;
      
      const tileUrl = this.buildTileUrl(layer.element, layer.model, time);
      layer.leafletLayer.setUrl(tileUrl);
    },
    
    buildTileUrl(element, model, time) {
      const sourceParam = model && model !== 'default' ? `&source=${model}` : '';
      return `${this.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/tile/` +
             `${element}/{z}/{x}/{y}.webp` +
             `?key=${this.weatherApiKey}` +
             `&time=${this.formatFullDateTime(time)}` +
             `&unitGroup=${this.unitGroup}` +
             `${sourceParam}`;
    },
    
    refreshAllLayers() {
      this.activeLayers.forEach(layer => {
        if (layer.visible && layer.leafletLayer) {
          this.updateLayerTiles(layer, this.currentTime);
        }
      });
      this.updateAllLegends();
    },
    
    changeBaseMap() {
      if (this.baseLayer) {
        this.map.removeLayer(this.baseLayer);
      }
      
      const config = this.baseMaps[this.selectedBaseMap];
      this.baseLayer = L.tileLayer(config.url, {
        subdomains: 'abcd',
        maxZoom: 19,
        attribution: config.attribution
      }).addTo(this.map);
      
      if (this.showLabels) {
        this.addLabelsLayer();
      }
    },
    
    toggleLabels() {
      if (this.showLabels) {
        this.addLabelsLayer();
      } else {
        if (this.labelLayer) {
          this.map.removeLayer(this.labelLayer);
          this.labelLayer = null;
        }
      }
    },
    
    addLabelsLayer() {
      if (this.labelLayer) {
        this.map.removeLayer(this.labelLayer);
      }
      
      const isDark = this.selectedBaseMap === 'carto-dark';
      const labelsUrl = isDark 
        ? 'https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png'
        : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png';
      
      this.labelLayer = L.tileLayer(labelsUrl, {
        subdomains: 'abcd',
        maxZoom: 19,
        pane: 'labelsPane',
        attribution: '&copy; <a href="https://carto.com">CARTO</a>'
      }).addTo(this.map);
    },
    
    async updateAllLegends() {
      const visibleLayers = this.activeLayers.filter(l => l.visible && l.leafletLayer);
      
      if (visibleLayers.length === 0) {
        this.visibleLayerLegends = [];
        return;
      }
      
      const legendPromises = visibleLayers.map(async (layer) => {
        const legendUrl = `${this.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/legend/` +
                          `${layer.element}?key=${this.weatherApiKey}&unitGroup=${this.unitGroup}`;
        
        try {
          const response = await fetch(legendUrl);
          const raw = await response.json();

          // Handle both single array of bins and array of arrays of bins
          let binGroups;
          if (Array.isArray(raw) && raw.length > 0 && Array.isArray(raw[0])) {
            // Response is array of arrays - keep as separate groups
            binGroups = raw;
          } else {
            // Single array - wrap in array for consistent handling
            binGroups = [raw];
          }
          
          // Cache flattened bins for color lookups
          this.elementBinCache[layer.element] = binGroups.flat();
          
          let html = '';
          binGroups.forEach((bins, groupIndex) => {
            if (groupIndex > 0) {
              html += '<div style="margin-top:8px;border-top:1px solid #eee;padding-top:8px;"></div>';
            }
            bins.forEach((bin, index) => {
              if (!bin || !bin.color) return;
              const hasLabel = !!bin.label;
              const label = bin.label || (bin.min ?? '');
              
              if (hasLabel) {
                const textColor = this.getContrastingTextColor(bin.color);
                html += `<div style="display:inline-flex;align-items:center;margin:2px 4px 2px 0;background:${bin.color};color:${textColor};padding:2px 8px;border-radius:10px;font-size:11px;font-weight:500;">${label}</div>`;
              } else {
                html += `<div style="display:inline-block;text-align:center;margin-right:5px;vertical-align:top;">
                          <div style="background-color:${bin.color};width:20px;height:20px;"></div>
                          ${index % Math.max(1, Math.ceil(bins.length / 10)) === 0 ? `<span style="font-size:10px;">${label}</span>` : ''}
                        </div>`;
              }
              
            });
          });
          
          return {
            layerId: layer.id,
            title: `${this.getModelDisplayName(layer.model)} - ${this.getElementDisplayName(layer.element)}`,
            html: html
          };
        } catch (error) {
          console.error('Error loading legend for', layer.element, error);
          return null;
        }
      });
      
      const results = await Promise.all(legendPromises);
      this.visibleLayerLegends = results.filter(r => r !== null);
    },
    
    toggleGeoJsonLayer() {
      if (this.showGeoJson) {
        if (!this.geoJsonLayer) {
          this.addGeoJsonTileLayer();
        } else {
          this.map.getPane('citiesPane').style.display = '';
          this.refreshGeoJsonTiles();
        }
      } else {
        if (this.map.getPane('citiesPane')) {
          this.map.getPane('citiesPane').style.display = 'none';
        }
        this.geoJsonAbortControllers.forEach(ctrl => ctrl.abort());
        this.geoJsonAbortControllers.clear();
      }
    },
    
     addGeoJsonTileLayer() {
      if (!this.map || this.geoJsonLayer) return;
      
      const self = this;
      const GeoJSONGridLayer = L.GridLayer.extend({
        createTile: function(coords, done) {
          const tile = document.createElement('div');
          const key = `${coords.z}/${coords.x}/${coords.y}`;
          
          if (self.geoJsonAbortControllers.has(key)) {
            self.geoJsonAbortControllers.get(key).abort();
          }
          
          if (self.geoJsonTiles[key]) {
            done(null, tile);
            return tile;
          }
          
          const controller = new AbortController();
          self.geoJsonAbortControllers.set(key, controller);
          
          const url = `${self.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/tile/` +
                      `current/${coords.z}/${coords.x}/${coords.y}.geojson` +
                      `?key=${self.weatherApiKey}&unitGroup=${self.unitGroup}`;
          
          fetch(url, { signal: controller.signal })
            .then(r => r.json())
            .then(fc => {
              if (!fc || !fc.features || !self.showGeoJson || !self.map) return;
              
              if (self.geoJsonTiles[key]) {
                self.map.removeLayer(self.geoJsonTiles[key]);
              }
              
              const tileGroup = L.layerGroup();
              
                            fc.features.forEach(feature => {
                if (feature.geometry.type !== 'Point') return;
                
                const [lng, lat] = feature.geometry.coordinates;
                const props = feature.properties || {};
                const cc = props.data?.currentConditions || {};
                
                // Get display value based on selected element
                const displayInfo = self.getGeoJsonDisplayInfo(cc);
                if (!displayInfo) return;
                
                const cityName = props.resolvedAddress || `${lat.toFixed(2)},${lng.toFixed(2)}`;
                
                // Build tooltip text
                const titleText = `${cityName}\nTemp: ${cc.temp ?? 'N/A'}°\nPrecip: ${cc.precip ?? 'N/A'}\nWind: ${cc.windspeed ?? 'N/A'} ${self.getWindDirectionLabel(cc.winddir)}`;
                
                let markerHtml;
                if (displayInfo.isWind) {
                  // Wind marker with arrow
                  const arrowRotation = displayInfo.winddir; // Arrow points in direction wind is going TO
                  markerHtml = `
                    <div class="wind-marker" title="${titleText.replace(/"/g, '&quot;')}" style="position:relative;width:44px;height:44px;">
                      <div style="position:absolute;top:8px;left:8px;background:${displayInfo.fill};color:${displayInfo.textColor};width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:500;">${displayInfo.label}</div>
                      <svg style="position:absolute;top:0;left:0;width:44px;height:44px;transform:rotate(${arrowRotation}deg);" viewBox="0 0 44 44">
                        <path d="M22 1 L27 10 L22 7 L17 10 Z" fill="${displayInfo.textColor}" stroke="${displayInfo.fill}" stroke-width="1"/>
                      </svg>
                    </div>`;
                } else {
                  markerHtml = `<div class="circle-marker" title="${titleText.replace(/"/g, '&quot;')}" style="background:${displayInfo.fill};color:${displayInfo.textColor};width:${displayInfo.width}px;height:28px;border-radius:14px;display:flex;align-items:center;justify-content:center;text-align:center;cursor:pointer;font-size:11px;padding:0 4px;white-space:nowrap;">${displayInfo.label}</div>`;
                }
                
                const icon = L.divIcon({
                  className: 'city-marker-icon',
                  html: markerHtml,
                  iconSize: displayInfo.isWind ? [44, 44] : [displayInfo.width, 28],
                  iconAnchor: displayInfo.isWind ? [22, 22] : [displayInfo.width / 2, 14]
                });
                
                const marker = L.marker([lat, lng], { icon, pane: 'citiesPane', interactive: false });
                
                tileGroup.addLayer(marker);
              });
              
              self.geoJsonTiles[key] = tileGroup;
              if (self.map && self.showGeoJson) {
                tileGroup.addTo(self.map);
              }
              done(null, tile);
            })
            .catch(err => {
              if (err.name !== 'AbortError') console.warn('GeoJSON tile error:', err);
              done(err, tile);
            })
            .finally(() => {
              self.geoJsonAbortControllers.delete(key);
            });
          
          return tile;
        },
        
        _removeTile: function(key) {
          // Clean up markers when tile is removed
          const coords = this._tiles[key]?.coords;
          if (coords) {
            const tileKey = `${coords.z}/${coords.x}/${coords.y}`;
            if (self.geoJsonTiles[tileKey]) {
              if (self.map) {
                self.map.removeLayer(self.geoJsonTiles[tileKey]);
              }
              delete self.geoJsonTiles[tileKey];
            }
            if (self.geoJsonAbortControllers.has(tileKey)) {
              self.geoJsonAbortControllers.get(tileKey).abort();
              self.geoJsonAbortControllers.delete(tileKey);
            }
          }
          L.GridLayer.prototype._removeTile.call(this, key);
        }
      });
      
      this.geoJsonLayer = new GeoJSONGridLayer({
        minZoom: 5,
        maxZoom: 18,
        updateWhenIdle: true,
        updateWhenZooming: false,
        keepBuffer: 2
      });
      
      this.geoJsonLayer.addTo(this.map);
    },

    getGeoJsonDisplayInfo(cc) {
      const element = this.geoJsonElement;
      
      if (element === 'temp') {
        const temp = cc.temp;
        if (temp == null) return null;
        const fill = this.getColor(temp, 'temp');
        const textColor = this.getContrastingTextColor(fill);
        return { label: Math.round(temp) + '°', fill, textColor, width: 28, isWind: false };
      } 
      else if (element === 'precip') {
        const precip = cc.precip;
        if (precip == null) return null;
        // Use a blue gradient for precip
        const fill = precip > 0 ? this.getPrecipColor(precip) : '#e0e0e0';
        const textColor = this.getContrastingTextColor(fill);
        return { label: precip.toFixed(1), fill, textColor, width: 36, isWind: false };
      } 
      else if (element === 'wind') {
        const windspeed = cc.windspeed;
        const winddir = cc.winddir;
        if (windspeed == null) return null;
        const fill = this.getWindColor(windspeed);
        const textColor = this.getContrastingTextColor(fill);
        return { 
          label: Math.round(windspeed), 
          fill, 
          textColor, 
          width: 36, 
          isWind: true,
          winddir: winddir ?? 0
        };
      }
      
      return null;
    },
    
    getWindDirectionLabel(degrees) {
      if (degrees == null) return '';
      const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
      const index = Math.round(degrees / 45) % 8;
      return directions[index];
    },
    
    getPrecipColor(precip) {
      // Simple precipitation color scale
      if (precip <= 0) return '#e0e0e0';
      if (precip < 0.5) return '#a8d5e5';
      if (precip < 2) return '#5fb3d4';
      if (precip < 5) return '#3498db';
      if (precip < 10) return '#2471a3';
      return '#1a4971';
    },
    
    getWindColor(windspeed) {
      // Wind speed color scale (assuming metric m/s or us mph based on unitGroup)
      if (windspeed < 5) return '#c8e6c9';
      if (windspeed < 10) return '#81c784';
      if (windspeed < 15) return '#ffd54f';
      if (windspeed < 25) return '#ff9800';
      if (windspeed < 40) return '#f44336';
      return '#9c27b0';
    },
    refreshGeoJsonTiles() {
      if (!this.geoJsonLayer || !this.showGeoJson) return;
      
      Object.keys(this.geoJsonTiles).forEach(key => {
        if (this.geoJsonTiles[key]) {
          this.map.removeLayer(this.geoJsonTiles[key]);
        }
      });
      this.geoJsonTiles = {};
      this.geoJsonAbortControllers.forEach(ctrl => ctrl.abort());
      this.geoJsonAbortControllers.clear();
      this.geoJsonLayer.redraw();
    },
    
    toggleMoviePlay() {
      this.isPlaying ? this.stopMovie() : this.startMovie();
    },
    
    startMovie() {
      this.isPlaying = true;
      this.movieInterval = setInterval(() => {
        this.incrementTime();
      }, 1000 / this.playbackSpeed);
    },
    
    stopMovie() {
      this.isPlaying = false;
      if (this.movieInterval) {
        clearInterval(this.movieInterval);
        this.movieInterval = null;
      }
    },
    
    adjustPlaybackSpeed() {
      if (this.isPlaying) {
        this.stopMovie();
        this.startMovie();
      }
    },
    
    increaseTimeSkip() {
      const currentIndex = this.timeSkipPresets.indexOf(this.manualTimeSkip);
      if (currentIndex < this.timeSkipPresets.length - 1) {
        this.manualTimeSkip = this.timeSkipPresets[currentIndex + 1];
      } else if (this.manualTimeSkip < 120) {
        this.manualTimeSkip = Math.min(120, this.manualTimeSkip + 30);
      }
    },
    
    decreaseTimeSkip() {
      const currentIndex = this.timeSkipPresets.indexOf(this.manualTimeSkip);
      if (currentIndex > 0) {
        this.manualTimeSkip = this.timeSkipPresets[currentIndex - 1];
      } else if (this.manualTimeSkip > 1) {
        this.manualTimeSkip = Math.max(1, this.manualTimeSkip - 1);
      }
    },
    
    incrementTime() {
      this.currentTime = this.calculateNewTime(this.currentTime, this.currentTimeSkip);
      this.refreshAllLayers();
    },
    
    decrementTime() {
      this.currentTime = this.calculateNewTime(this.currentTime, -this.currentTimeSkip);
      this.refreshAllLayers();
    },
    
    resetToInitialTime() {
      this.currentTime = this.getLastInterval();
      this.refreshAllLayers();
    },
    
    calculateNewTime(time, deltaMinutes) {
      const newTime = new Date(time);
      newTime.setUTCMinutes(newTime.getUTCMinutes() + deltaMinutes);
      return newTime;
    },
    
    getLastInterval() {
      const now = new Date();
      const minutes = Math.floor(now.getUTCMinutes() /  this.currentTimeSkip) *  this.currentTimeSkip;
      now.setUTCMinutes(minutes, 0, 0);
      return now;
    },
    
    onDateTimeInput(e) {
      this.tempDateTimeValue = e.target.value;
    },
    
    applyDateTime() {
      if (this.tempDateTimeValue) {
        const date = new Date(this.tempDateTimeValue + ':00Z');
        this.currentTime = date;
        this.refreshAllLayers();
      }
      this.showDateTimePicker = false;
    },
    
    formatFullDateTime(date) {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:00`;
    },
    
    updateViewInfo() {
      if (!this.map) return;
      const c = this.map.getCenter();
      this.viewInfo.lat = Number(c.lat.toFixed(5));
      this.viewInfo.lng = Number(c.lng.toFixed(5));
      this.viewInfo.zoom = this.map.getZoom();
      this.viewInfo.centerPair = `${this.viewInfo.lat},${this.viewInfo.lng}`;
    },
    
    async copyCenterToClipboard() {
      try {
        await navigator.clipboard.writeText(this.viewInfo.centerPair);
      } catch (e) {
        console.warn('Copy failed', e);
      }
    },
    
    getColor(value, element) {
      if (this.elementBinCache[element]) {
        const bins = this.elementBinCache[element];
        for (let bin of bins) {
          if (value >= bin.min && (bin.max == null || value < bin.max)) {
            return bin.color;
          }
        }
      }
      return '#CFCFCF';
    },
    
   getContrastingTextColor(hex) {
      if (!hex) return '#000000';
      const h = hex.replace('#', '');
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return L > 0.5 ? '#000000' : '#FFFFFF';
    },
    
    saveSettings() {
      try {
        const settings = {
          unitGroup: this.unitGroup,
          baseApiUrl: this.baseApiUrl,
          selectedBaseMap: this.selectedBaseMap,
          showLabels: this.showLabels,
          showGeoJson: this.showGeoJson,
          geoJsonElement: this.geoJsonElement,
          playbackSpeed: this.playbackSpeed,
          autoTimeSkip: this.autoTimeSkip,
          manualTimeSkip: this.manualTimeSkip,
          activeLayers: this.activeLayers.map(layer => ({
            id: layer.id,
            model: layer.model,
            element: layer.element,
            visible: layer.visible,
            opacity: layer.opacity
          })),
          nextLayerId: this.nextLayerId
        };
        localStorage.setItem('weatherMapSettings', JSON.stringify(settings));
      } catch (e) {
        console.warn('Failed to save settings:', e);
      }
    },
    
    loadSettings() {
      try {
        const saved = localStorage.getItem('weatherMapSettings');
        if (!saved) return;
        
        const settings = JSON.parse(saved);
        
        if (settings.unitGroup) this.unitGroup = settings.unitGroup;
        if (settings.baseApiUrl) this.baseApiUrl = settings.baseApiUrl;
        if (settings.selectedBaseMap) this.selectedBaseMap = settings.selectedBaseMap;
        if (typeof settings.showLabels === 'boolean') this.showLabels = settings.showLabels;
        if (typeof settings.showGeoJson === 'boolean') this.showGeoJson = settings.showGeoJson;
        if (settings.geoJsonElement) this.geoJsonElement = settings.geoJsonElement;
        if (settings.playbackSpeed) this.playbackSpeed = settings.playbackSpeed;
        if (typeof settings.autoTimeSkip === 'boolean') this.autoTimeSkip = settings.autoTimeSkip;
        if (settings.manualTimeSkip) this.manualTimeSkip = settings.manualTimeSkip;
        if (settings.nextLayerId) this.nextLayerId = settings.nextLayerId;
        
        if (Array.isArray(settings.activeLayers)) {
          this.activeLayers = settings.activeLayers.map(layer => ({
            ...layer,
            leafletLayer: null // Will be created after map init
          }));
        }
      } catch (e) {
        console.warn('Failed to load settings:', e);
      }
    }
  }
};
</script>

<style scoped>
.weather-map-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.map-view {
  width: 100%;
  height: 100%;
}

.map-controls {
  position: absolute;
  top: 10px;
  left: 50px;
  z-index: 1000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  max-width: 300px;
  transition: transform 0.1s ease, opacity 0.1s ease;
}

/* Hide controls when collapsed on desktop */
.map-controls:not(.controls-open) {
  transform: translateX(-110%);
  opacity: 0;
  pointer-events: none;
}

.map-controls.controls-open {
  transform: translateX(0);
  opacity: 1;
  pointer-events: auto;
}

.controls-toggle {
  display: none;
  position: absolute;
  top: 10px;
  left: 10px;
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  font-size: 20px;
  z-index: 1001;
  cursor: pointer;
}





.controls-panel {
  padding: 15px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.control-group {
  margin-bottom: 15px;
}

.control-group label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
}
.control-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.control-row .control-group {
  margin-bottom: 0;
}

.control-group.compact {
  margin-bottom: 8px;
}

.control-group.compact label {
  margin-bottom: 3px;
}
.layer-list {
  max-height: 250px;
  overflow-y: auto;
}

.layer-item {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.layer-item:last-child {
  border-bottom: none;
}

.layer-controls {
  padding-left: 28px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-range-sm {
  height: 0.5rem;
}

.time-display {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 15px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 6px;
}

.time-display:hover {
  background: white;
}

.datetime-picker-modal,
.add-layer-modal {
  position: fixed;  /* Changed from absolute to fixed */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content-custom {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  min-width: 280px;
  max-width: 90vw;
  position: relative;  /* Ensure content is above backdrop */
  z-index: 2001;
}

.modal-content-custom h6 {
  margin-bottom: 15px;
  font-weight: 600;
}

.advanced-header {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 0;
}

.advanced-header:hover {
  opacity: 0.8;
}

.advanced-content {
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
}

.legend-container {
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  max-width: 320px;
  z-index: 1000;
}

.legend-header {
  padding: 10px 15px;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.legend-collapsed .legend-header {
  border-radius: 8px;
}

.legend-content {
  padding: 5px 0;
  max-height: 300px;
  overflow-y: auto;
}

.legend-item {
  border-bottom: 1px solid #eee;
}

.legend-item:last-child {
  border-bottom: none;
}

.legend-item-header {
  padding: 8px 15px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.legend-item-header:hover {
  background: #f8f9fa;
}

.legend-item-title {
  font-size: 12px;
  font-weight: 500;
}

.legend-item-content {
  padding: 5px 15px 10px 15px;
}
.time-skip-control {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
}

.time-skip-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.time-skip-display.auto-mode {
  opacity: 0.7;
}

.time-skip-value {
  display: flex;
  align-items: baseline;
  gap: 2px;
  min-width: 60px;
  justify-content: center;
}

.skip-number {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}
.controls-panel::-webkit-scrollbar,
.layer-list::-webkit-scrollbar,
.legend-content::-webkit-scrollbar {
  width: 6px;
}

.controls-panel::-webkit-scrollbar-track,
.layer-list::-webkit-scrollbar-track,
.legend-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.controls-panel::-webkit-scrollbar-thumb,
.layer-list::-webkit-scrollbar-thumb,
.legend-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

@media (max-width: 768px) {
  .controls-toggle {
    display: block;
  }
  
  .map-controls {
    top: 60px;
    left: 10px;
    max-width: 280px;
    z-index: 1100;  /* Lower z-index so modals appear above */
  }
  
  .layers-quick-toggle {
    display: none;
  }
  
  .controls-panel {
    max-height: calc(100vh - 20px);
  }
  
  .time-display {
    font-size: 12px;
    padding: 6px 10px;
  }
  
  .legend-container {
    bottom: 10px;
    right: 10px;
    max-width: 200px;
  }
  
  .legend-content {
    max-height: 150px;
  }
}

@media (max-width: 480px) {
  .map-controls {
    max-width: calc(100vw - 20px);
  }
  
  .time-display {
    right: 60px;
  }
}
</style>


<style>
/* Leaflet layers control button - not scoped */
.layers-control-btn {
  width: 30px !important;
  height: 30px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: #333;
}

.layers-control-btn:hover {
  background-color: #f4f4f4;
  color: #333;
}

.layers-control-btn svg {
  width: 16px;
  height: 16px;
}
.city-marker-popup .leaflet-popup-content-wrapper {
  padding: 4px 8px;
  border-radius: 6px;
}

.city-marker-popup .leaflet-popup-content {
  margin: 6px;
  font-size: 12px;
}

.city-marker-popup .leaflet-popup-tip-container {
  display: none;
}
</style>